import React, { createContext, useContext, useState, ReactNode } from 'react';
import { EditorElement } from '../types/elements';

interface EditorContextType {
  elements: EditorElement[];
  setElements: React.Dispatch<React.SetStateAction<EditorElement[]>>;
  addElement: (element: EditorElement) => void;
  updateElement: (id: string, updates: Partial<EditorElement>) => void;
  removeElement: (id: string) => void;
  moveElement: (id: string, direction: 'up' | 'down') => void;
  duplicateElement: (id: string) => void;
  deleteElement: (id: string) => void;
}

const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const useEditor = () => {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor must be used within EditorProvider');
  return ctx;
};

export const EditorProvider = ({ children }: { children: ReactNode }) => {
  const [elements, setElements] = useState<EditorElement[]>([]);

  const addElement = (element: EditorElement) => {
    setElements((prev) => [...prev, element]);
  };

  const updateElement = (id: string, updates: Partial<EditorElement>) => {
    setElements((prev) =>
      prev.map((el) => {
        if (el.id !== id) return el;
        const { type, ...restUpdates } = updates;
        return { ...el, ...restUpdates };
      })
    );
  };

  const removeElement = (id: string) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
  };

  const moveElement = (id: string, direction: 'up' | 'down') => {
    setElements((prev) => {
      const idx = prev.findIndex((el) => el.id === id);
      if (idx === -1) return prev;
      const newArr = [...prev];
      if (direction === 'up' && idx > 0) {
        [newArr[idx - 1], newArr[idx]] = [newArr[idx], newArr[idx - 1]];
      } else if (direction === 'down' && idx < prev.length - 1) {
        [newArr[idx + 1], newArr[idx]] = [newArr[idx], newArr[idx + 1]];
      }
      return newArr.map((el, i) => ({ ...el, order: i }));
    });
  };

  const duplicateElement = (id: string) => {
    setElements((prev) => {
      const el = prev.find(e => e.id === id);
      if (!el) return prev;
      const newEl = { ...el, id: Date.now().toString(), order: prev.length, x: el.x + 20, y: el.y + 20 };
      return [...prev, newEl];
    });
  };

  const deleteElement = (id: string) => {
    setElements((prev) => prev.filter(e => e.id !== id));
  };

  return (
    <EditorContext.Provider value={{ elements, setElements, addElement, updateElement, removeElement, moveElement, duplicateElement, deleteElement }}>
      {children}
    </EditorContext.Provider>
  );
};
